import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './OurServices.css';

const interiorItems = [
  { title: 'Design Concepts', desc: 'Including spatial planning and visual renderings, if applicable' },
  { title: 'Refresh & Redecorate', desc: 'Decorate or refresh an existing space with new life' },
  { title: 'New Space Design', desc: 'Design and decorate a new space from the ground up' },
  { title: 'Sourcing', desc: 'Source unique furnishings and decor tailored to your style' },
  { title: 'Contractor Coordination', desc: 'Coordinate with contractors during renovation projects' },
];

const orgItems = [
  { title: 'Space Organization', desc: 'Organize existing spaces — kitchen, closets, garages, and more' },
  { title: 'Item Removal', desc: 'Coordinate donation, sale, or removal of existing items' },
];

function ServiceItem({ item, index, inView }) {
  return (
    <motion.div
      className="service-item"
      initial={{ opacity: 0, y: 25 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.15 + index * 0.1, ease: [0.4, 0, 0.2, 1] }}
    >
      <span className="service-item-number">{String(index + 1).padStart(2, '0')}</span>
      <div className="service-item-content">
        <h4>{item.title}</h4>
        <p>{item.desc}</p>
      </div>
    </motion.div>
  );
}

function OurServices() {
  const [interiorRef, interiorInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [orgRef, orgInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.div
      className="services-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero */}
      <section className="services-hero">
        <div className="services-hero-overlay" />
        <motion.div
          className="services-hero-content"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.77, 0, 0.175, 1] }}
        >
          <motion.span
            className="services-hero-label"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            What We Do
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.77, 0, 0.175, 1] }}
          >
            Our Services
          </motion.h1>
          <motion.div
            className="services-hero-line"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.8, ease: [0.77, 0, 0.175, 1] }}
          />
        </motion.div>
      </section>

      {/* Interior Design */}
      <section className="services-section" ref={interiorRef}>
        <div className="services-section-inner">
          <motion.div
            className="services-section-header"
            initial={{ opacity: 0, y: 30 }}
            animate={interiorInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <span className="services-section-label">01</span>
            <h2>Interior Design</h2>
            <p className="services-section-tagline">
              Spaces designed around the way you actually live.
            </p>
          </motion.div>
          <div className="services-list">
            {interiorItems.map((item, i) => (
              <ServiceItem key={item.title} item={item} index={i} inView={interiorInView} />
            ))}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="services-divider-section">
        <motion.div
          className="services-divider"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.77, 0, 0.175, 1] }}
        />
      </div>

      {/* Home Organization */}
      <section className="services-section services-section-alt" ref={orgRef}>
        <div className="services-section-inner">
          <motion.div
            className="services-section-header"
            initial={{ opacity: 0, y: 30 }}
            animate={orgInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <span className="services-section-label">02</span>
            <h2>Home Organization</h2>
            <p className="services-section-tagline">
              Calm, order, and breathing room — in every corner of your home.
            </p>
          </motion.div>
          <div className="services-list">
            {orgItems.map((item, i) => (
              <ServiceItem key={item.title} item={item} index={i} inView={orgInView} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="services-cta">
        <motion.div
          className="services-cta-line"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: [0.77, 0, 0.175, 1] }}
        />
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Let's Get Started
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          Every great space starts with a conversation.
        </motion.p>
        <motion.a
          href="mailto:hello@casaprimas.com"
          className="cta-button"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          Get in Touch
        </motion.a>
      </section>
    </motion.div>
  );
}

export default OurServices;
